// Pure copy helpers for the RP-5 impersonation banner. Lives in a
// standalone file (rather than inline in `ActivityDockExpandedPanel.tsx`
// or `ActivityDockTab.tsx`) so the copy decisions are unit-testable in
// the Node-environment vitest setup without dragging in React /
// jsdom. See docs/planning/firefly_role_context_presentation.md
// §RP-5 (closes L4).
//
// Doctrine notes:
//   - The banner must be non-dismissable for the session (§5.4 last
//     row, §6 R5). These helpers do not encode dismissability; that's
//     the caller's responsibility (render the banner whenever
//     `impersonationActive` is true, no close button rendered).
//   - The collapsed-tab aria-label must communicate impersonation
//     state for screen-reader users — the chrome-only purple outline
//     is invisible to them. `formatImpersonationAriaSuffix` returns
//     the suffix the tab's existing `ariaLabel` should append.

/**
 * Returns the banner copy to render when impersonation is active.
 * Returns `null` when impersonation is inactive OR when the role
 * list is empty (defensive — the auth layer should never publish
 * `impersonation.active === true` without a role list, but we'd
 * rather render no banner than a banner with an empty value).
 */
export function formatImpersonationBannerCopy(
  active: boolean,
  roles: readonly string[],
): string | null {
  if (!active) return null;
  const trimmed = roles.map((r) => r.trim()).filter((r) => r.length > 0);
  if (trimmed.length === 0) return null;
  return `Impersonating: ${trimmed.join(", ")}`;
}

/**
 * Returns the suffix to append to the collapsed-tab `ariaLabel` when
 * impersonation is active, or an empty string otherwise. Kept narrow
 * so the caller can do `ariaLabel + suffix` without conditional
 * branching at the call site.
 */
export function formatImpersonationAriaSuffix(
  active: boolean,
  roles: readonly string[],
): string {
  const copy = formatImpersonationBannerCopy(active, roles);
  return copy ? ` — ${copy}` : "";
}
